import React from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Divider,
  IconButton,
  Box,
  Typography,
  Collapse,
  Badge
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Description as TemplateIcon,
  Settings as ConfigIcon,
  CheckCircle as ValidationIcon,
  ContentCopy as BulkIcon,
  Security as AuditIcon,
  LibraryBooks as LibraryIcon,
  CloudUpload as DeployIcon,
  Analytics as AnalyticsIcon,
  Psychology as ConversationalIcon,
  ChevronLeft,
  ChevronRight,
  ExpandLess,
  ExpandMore,
  Home
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';

interface NavigationProps {
  open: boolean;
  onToggle: () => void;
}

interface NavItem {
  text: string;
  icon: React.ReactElement;
  path: string;
  badge?: number;
  children?: NavItem[];
}

const Navigation: React.FC<NavigationProps> = ({ open, onToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedItems, setExpandedItems] = React.useState<string[]>([]);

  const drawerWidth = 240;
  const collapsedWidth = 64;

  const navItems: NavItem[] = [
    {
      text: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/dashboard'
    },
    {
      text: 'Templates',
      icon: <TemplateIcon />,
      path: '/templates',
      children: [
        {
          text: 'Create Template',
          icon: <TemplateIcon />,
          path: '/templates/create'
        },
        {
          text: 'Template Library',
          icon: <LibraryIcon />,
          path: '/templates/library'
        }
      ]
    },
    {
      text: 'Configurations',
      icon: <ConfigIcon />,
      path: '/configurations'
    },
    {
      text: 'AI Assistant',
      icon: <ConversationalIcon />,
      path: '/conversational',
      badge: undefined // Could add "NEW" badge later
    },
    {
      text: 'Validation',
      icon: <ValidationIcon />,
      path: '/validation'
    },
    {
      text: 'Bulk Operations',
      icon: <BulkIcon />,
      path: '/bulk-operations'
    },
    {
      text: 'Audit & Compliance',
      icon: <AuditIcon />,
      path: '/audit'
    },
    {
      text: 'Export & Deploy',
      icon: <DeployIcon />,
      path: '/export-deploy'
    },
    {
      text: 'Analytics',
      icon: <AnalyticsIcon />,
      path: '/analytics'
    }
  ];

  const handleNavClick = (item: NavItem) => {
    if (item.children) {
      const isExpanded = expandedItems.includes(item.text);
      setExpandedItems(
        isExpanded
          ? expandedItems.filter(i => i !== item.text)
          : [...expandedItems, item.text]
      );
    } else {
      navigate(item.path);
    }
  };

  const isActive = (path: string) => location.pathname === path;
  const isParentActive = (item: NavItem) => {
    if (item.children) {
      return item.children.some(child => isActive(child.path));
    }
    return isActive(item.path);
  };

  const renderNavItem = (item: NavItem, depth: number = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.includes(item.text);
    const active = isParentActive(item);

    return (
      <React.Fragment key={item.text}>
        <ListItem disablePadding sx={{ display: 'block' }}>
          <ListItemButton
            onClick={() => handleNavClick(item)}
            sx={{
              minHeight: 48,
              justifyContent: open ? 'initial' : 'center',
              px: 2.5,
              pl: depth > 0 ? 4 : 2.5,
              backgroundColor: active ? 'action.selected' : 'transparent',
              '&:hover': {
                backgroundColor: 'action.hover'
              }
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: open ? 3 : 'auto',
                justifyContent: 'center',
                color: active ? 'primary.main' : 'inherit'
              }}
            >
              {item.badge ? (
                <Badge badgeContent={item.badge} color="error">
                  {item.icon}
                </Badge>
              ) : (
                item.icon
              )}
            </ListItemIcon>
            <ListItemText
              primary={item.text}
              sx={{
                opacity: open ? 1 : 0,
                color: active ? 'primary.main' : 'inherit'
              }}
            />
            {hasChildren && open && (
              isExpanded ? <ExpandLess /> : <ExpandMore />
            )}
          </ListItemButton>
        </ListItem>
        {hasChildren && (
          <Collapse in={isExpanded && open} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children!.map(child => renderNavItem(child, depth + 1))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: open ? drawerWidth : collapsedWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: open ? drawerWidth : collapsedWidth,
          boxSizing: 'border-box',
          transition: theme => theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          overflowX: 'hidden'
        },
      }}
    >
      <Toolbar sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        px: 2
      }}>
        {open && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Home color="primary" />
            <Typography variant="h6" noWrap component="div">
              Netrium
            </Typography>
          </Box>
        )}
        <IconButton onClick={onToggle} size="small">
          {open ? <ChevronLeft /> : <ChevronRight />}
        </IconButton>
      </Toolbar>
      <Divider />
      <List>
        {navItems.map(item => renderNavItem(item))}
      </List>
    </Drawer>
  );
};

export default Navigation;